// The client side of POST /api/characters/{slug}:migrateMaterialsToGraph. The
// server does the work and is idempotent; this shows its report BEFORE writing
// (dry run) and AFTER, and reloads the avatar so the re-pointed mapping shows.
// Pure formatting + an injected flow, so the decision tree is unit-tested.

export function formatMigrationReport(r) {
  const n = r.converted ?? 0
  if (n === 0 && (r.rows?.length ?? 0) === 0) {
    return { title: 'Nothing to convert', body: `${r.character}: every material is already a graph.` }
  }
  const verb = r.dryRun ? 'would be converted' : 'converted'
  const title = `${n} material${n === 1 ? '' : 's'} ${verb}${r.skipped ? `, ${r.skipped} already migrated` : ''}${r.failed ? `, ${r.failed} FAILED` : ''}`
  const lines = []
  for (const row of r.rows ?? []) {
    if (row.skipped) { lines.push(`• ${row.name} — already migrated`); continue }
    if (row.error) { lines.push(`• ${row.name} — FAILED: ${row.error}`); continue }
    const slots = row.mappingEntriesRepointed ?? 0
    lines.push(`• ${row.name} → ${row.preset} preset, ${slots} slot${slots === 1 ? '' : 's'} re-pointed`)
    for (const w of row.warnings ?? []) lines.push(`    ⚠ ${w}`)
  }
  if (r.dryRun) lines.push('', 'Originals are kept under /materials/deprecated as "(legacy)" for comparison. Nothing is deleted.')
  return { title, body: lines.join('\n') }
}

export async function runMaterialMigration({ migrate, confirm, alert, reload }) {
  let dry
  try {
    dry = await migrate({ dryRun: true })
  } catch (e) {
    await alert({ title: 'Convert materials failed', body: e?.message ?? String(e), variant: 'error' })
    return null
  }
  const preview = formatMigrationReport(dry)
  if ((dry.converted ?? 0) === 0) { await alert({ ...preview, variant: 'info' }); return dry }
  const ok = await confirm({ ...preview, confirmLabel: 'Convert' })
  if (!ok) return null
  let result
  try {
    result = await migrate({ dryRun: false })
  } catch (e) {
    await alert({ title: 'Convert materials failed', body: e?.message ?? String(e), variant: 'error' })
    return null
  }
  await reload()
  const after = formatMigrationReport(result)
  await alert({ ...after, variant: result.failed ? 'error' : 'info' })
  return result
}
